import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@blueprintjs/core"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import {
  MAP_STYLES,
  MapStyle,
  MapStyleEntry,
} from "@/components/maps/MapManager"
import ShowEntireRouteButton from "@/components/maps/ShowEntireRouteButton"

type AgentMapControlsProps = {
  currentStyle: MapStyle
  onChangeStyle: (style: MapStyle) => void
  onFitAll: () => void
  canFitAll: boolean
  onToggleFullscreen: () => void
  isFullscreen: boolean
  // Optional: when provided, a 4th button is rendered below the
  // fullscreen toggle that dispatches this callback (used to dismiss
  // the entire map widget from the inline panel). Not used in the
  // fullscreen dialog — there the dialog has its own close.
  onCloseWidget?: () => void
  // Extra className for the style-picker PopoverContent. Used inside
  // the fullscreen Dialog (which sits at z-[1010]) to bump the popover
  // above it; otherwise the popover renders behind the dialog.
  popoverContentClassName?: string
}

const AgentMapControls = ({
  currentStyle,
  onChangeStyle,
  onFitAll,
  canFitAll,
  onToggleFullscreen,
  isFullscreen,
  onCloseWidget,
  popoverContentClassName,
}: AgentMapControlsProps) => {
  const { t } = useTranslation()
  const [styleOpen, setStyleOpen] = useState(false)
  return (
    <>
      <div className="absolute left-[10px] top-[10px] z-10">
        {/* modal so the popover takes over pointer/focus when opened
            inside the fullscreen Dialog (which itself is modal and
            otherwise disables pointer events on body siblings). */}
        <Popover open={styleOpen} onOpenChange={setStyleOpen} modal>
          <PopoverTrigger asChild>
            <Button
              icon="map"
              className="dark:bg-gray-700 dark:text-white"
              title={t("ai.map.changeStyle")}
            />
          </PopoverTrigger>
          <PopoverContent
            className={cn("w-48 p-0", popoverContentClassName)}
            align="start"
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {Object.entries<MapStyleEntry>(MAP_STYLES).map(([key]) => (
                    <CommandItem
                      key={key}
                      onSelect={() => {
                        setStyleOpen(false)
                        onChangeStyle(key as MapStyle)
                      }}
                      className={cn(
                        currentStyle === key &&
                          "bg-accent text-accent-foreground",
                      )}
                    >
                      {t(`maps.changeMapStyle.styles.${key as MapStyle}`)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <ShowEntireRouteButton onClick={onFitAll} disabled={!canFitAll} />
      <div className="absolute left-[10px] top-[90px] z-10">
        <Button
          icon={isFullscreen ? "minimize" : "fullscreen"}
          className="dark:bg-gray-700 dark:text-white"
          title={
            isFullscreen
              ? t("ai.map.closeFullscreen")
              : t("ai.map.openFullscreen")
          }
          onClick={onToggleFullscreen}
        />
      </div>
      {onCloseWidget && (
        <div className="absolute left-[10px] top-[130px] z-10">
          <Button
            icon="cross"
            className="dark:bg-gray-700 dark:text-white"
            title={t("ai.map.closeWidget")}
            onClick={onCloseWidget}
          />
        </div>
      )}
    </>
  )
}

export default AgentMapControls
